import React, { useEffect, useState } from "react";
import MainModal from "components/Modal/MainModal";
import { toast } from "react-toastify";
import { MdEdit, MdAdd } from "react-icons/md";
import { adminApiService } from "services/api";

const FooterSettings = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [footerData, setFooterData] = useState({
    licenseNo: "",
    description: "",
  });
  const [editingField, setEditingField] = useState(null);
  const [isAddMode, setIsAddMode] = useState(false);

  const fetchFooterData = async () => {
    setIsLoading(true);
    try {
      const res = await adminApiService.getFooterSettings();
      if (!res.ok) throw new Error("Failed to fetch footer settings");
      const { data } = await res.json();
      setFooterData({
        licenseNo: data.license_no || "",
        description: data.description || "",
      });
    } catch (error) {
      console.error("Error fetching footer settings:", error);
      toast.error("Error fetching footer settings.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchFooterData();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFooterData((prev) => ({ ...prev, [name]: value }));
  };

  const openEditModal = (field) => {
    setIsAddMode(false);
    setEditingField(field);
    setIsModalOpen(true);
  };

  const openAddModal = (field) => {
    setIsAddMode(true);
    setEditingField(field);
    setFooterData((prev) => ({ ...prev, [field]: "" }));
    setIsModalOpen(true);
  };

  const handleSubmit = async () => {
    if (!footerData[editingField]?.trim()) {
      toast.error("Please fill in all required fields.");
      return;
    }

    try {
      // const endpoint = isAddMode ? "add" : "update";
      // const res = await fetch(
      //   `${process.env.REACT_APP_API_URL}/cms/${endpoint}-footer-settings`,
      //   {
      //     method: isAddMode ? "POST" : "PUT",
      //     headers: {
      //       "Content-Type": "application/json",
      //     },
      //     body: JSON.stringify({
      //       [editingField === "licenseNo" ? "license_no" : "description"]:
      //         footerData[editingField].trim(),
      //     }),
      //     credentials: "include",
      //   }
      // );

     const res= isAddMode ? await adminApiService.addFooterSettings(footerData) : await adminApiService.updateFooterSettings(footerData);

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(
          errorData.message ||
            `Failed to ${isAddMode ? "add" : "update"} footer settings`
        );
      }

      toast.success(
        `Footer settings ${isAddMode ? "added" : "updated"} successfully!`
      );
      setIsModalOpen(false);
      await fetchFooterData();
      setEditingField(null);
      setIsAddMode(false);
    } catch (error) {
      toast.error(
        `Error ${isAddMode ? "adding" : "updating"} footer settings: ${
          error.message
        }`
      );
      console.error("Submit error:", error);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-10 rounded-2xl bg-white p-8 shadow-sm">
          <div>
            <h1 className="text-4xl font-bold text-gray-900">
              Footer Settings
            </h1>
            <p className="mt-3 text-base text-gray-600">
              Manage footer content and license information
            </p>
          </div>
        </div>

        {/* Main Content */}
        <div className="mt-8">
          {isLoading ? (
            <div className="flex h-64 items-center justify-center">
              <div className="border-t-transparent h-12 w-12 animate-spin rounded-full border-4 border-primary"></div>
            </div>
          ) : (
            <div className="grid gap-8 sm:grid-cols-2">
              {/* License Number Section */}
              <div className="rounded-2xl bg-white p-8 shadow-sm hover:shadow-md">
                <div className="flex flex-col space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                      License Number
                    </h2>
                    <div className="flex space-x-3">
                      {!footerData.licenseNo && !footerData.description && (
                        <button
                          onClick={() => openAddModal("licenseNo")}
                          className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                          aria-label="Add license number"
                        >
                          <MdAdd size={24} />
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal("licenseNo")}
                        className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                        aria-label="Edit license number"
                      >
                        <MdEdit size={24} />
                      </button>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-50 p-4">
                    <p className="text-base text-gray-600">
                      {footerData.licenseNo || "No license number set"}
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer Description Section */}
              <div className="rounded-2xl bg-white p-8 shadow-sm transition-all duration-300 hover:shadow-md">
                <div className="flex flex-col space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-xl font-semibold text-gray-900">
                      Footer Description
                    </h2>
                    <div className="flex space-x-3">
                      {!footerData.description && !footerData.licenseNo && (
                        <button
                          onClick={() => openAddModal("description")}
                          className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                          aria-label="Add footer description"
                        >
                          <MdAdd size={24} />
                        </button>
                      )}
                      <button
                        onClick={() => openEditModal("description")}
                        className="rounded-xl p-2.5 text-primary transition-colors hover:bg-primary/10"
                        aria-label="Edit footer description"
                      >
                        <MdEdit size={24} />
                      </button>
                    </div>
                  </div>
                  <div className="rounded-xl bg-gray-50 p-4">
                    <p className="text-base text-gray-600">
                      {footerData.description || "No description set"}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      <MainModal
        isOpen={isModalOpen}
        setModalOpen={setIsModalOpen}
        title={`${isAddMode ? "Add" : "Edit"} ${
          editingField === "licenseNo" ? "License Number" : "Footer Description"
        }`}
        btnTitle={isAddMode ? "Add" : "Update"}
        actionbtn={handleSubmit}
      >
        <div className="space-y-6">
          <div>
            <label
              htmlFor={editingField}
              className="mb-3 block text-base font-medium text-gray-700"
            >
              {editingField === "licenseNo" ? "License Number" : "Description"}{" "}
              *
            </label>
            {editingField === "description" ? (
              <textarea
                id={editingField}
                name={editingField}
                value={footerData[editingField]}
                onChange={handleInputChange}
                rows={4}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                required
                placeholder={`Enter ${
                  editingField === "licenseNo"
                    ? "license number"
                    : "description"
                }...`}
              />
            ) : (
              <input
                type="text"
                id={editingField}
                name={editingField}
                value={footerData[editingField]}
                onChange={handleInputChange}
                className="w-full rounded-xl border border-gray-300 px-4 py-3 text-base focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary"
                required
                placeholder={`Enter ${
                  editingField === "licenseNo"
                    ? "license number"
                    : "description"
                }...`}
              />
            )}
          </div>
        </div>
      </MainModal>
    </div>
  );
};

export default FooterSettings;
